import React, { useEffect, useRef, useState } from "react";

/* ---------- Hook: in-view trigger (safe + fallback) ---------- */
function useInView({ threshold = 0.2, rootMargin = "0px 0px -15% 0px" } = {}) {
  const ref = useRef(null);
  const [active, setActive] = useState(true); // ✅ default TRUE so page never hides

  useEffect(() => {
    if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
      setActive(true);
      return;
    }

    const el = ref.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      ([entry]) => setActive(entry.isIntersecting),
      { threshold, rootMargin }
    );

    obs.observe(el);

    // ✅ also show immediately on mount (prevents blank page if observer delayed)
    setTimeout(() => setActive(true), 0);

    return () => obs.disconnect();
  }, [threshold, rootMargin]);

  return { ref, active };
}

/* ✅ Keep ALL links in ONE place */
const HOME_ASSETS = {
  hero: {
    image: "https://fogsv.com/wp-content/uploads/2022/09/fog-republic-2022-22.jpg",
    videoMp4: "/assets/videos/home-montage.mp4", // replace with real file
    youtube: "https://www.youtube.com/embed/TkxuAy4vdKY",
    useYoutube: false,
  },
  purpose: {
    image: "https://fogsv.com/wp-content/uploads/2022/09/fog-republic-2022-21.jpg",
  },
  atAGlance: {
    culture:
      "https://fogsv.com/wp-content/uploads/2022/09/fog-republic-2022-18-487x358.jpg",
    leaders: "https://fogsv.com/wp-content/uploads/2022/09/fog-republic-2022-19.jpg",
    civic: "https://fogsv.com/wp-content/uploads/2022/09/fog-republic-2022-20.jpg",
    alliances:
      "https://fogsv.com/wp-content/uploads/2022/09/fog-republic-2022-23.jpg",
  },
  donateUrl: "https://givebutter.com/Nnf9Vr",
};

function HomePage() {
  const { ref, active } = useInView();
  const heroVideoRef = useRef(null);

  useEffect(() => {
    if (HOME_ASSETS.hero.useYoutube) return;

    const v = heroVideoRef.current;
    if (!v) return;

    if (active) {
      v.preload = "auto";
      v.play().catch(() => {});
    } else {
      v.pause();
    }
  }, [active]);

  const handleDonate = () => window.open(HOME_ASSETS.donateUrl, "_blank");

  // ✅ You can replace onClick with navigate() later if needed
  const quickLinks = [
    {
      label:
        "Hindu Rajneeti Academy — World-class civic & political leadership training",
      onClick: () => {},
    },
    {
      label: "Online Courses — On-demand learning modules and structured pathways",
      onClick: () => {},
    },
    {
      label: "Hindu Voters Guide — Nonpartisan, issue-based voting resources",
      onClick: () => {},
    },
    {
      label: "Join Alliances — Multicultural & interfaith coalition-building",
      onClick: () => {},
    },
    {
      label: "Upcoming Events — Festivals, summits, classes, webinars",
      onClick: () => {},
    },
    {
      label: "Membership & Volunteering — Join, contribute, participate, empower",
      onClick: () => {},
    },
    {
      label: "Donate / Sponsor — Support cultural pride and civic leadership",
      onClick: handleDonate,
      primary: true,
    },
  ];

  return (
    <>
      <style>{`
        /* ✅ Keep lists left aligned */
        .section-title-body ul,
        .section-title-body ol,
        .section-title-body li {
          text-align: left !important;
        }

        .home-service-page .block.color-scheme-4 {
          padding-top: 56px !important;
          padding-bottom: 34px !important;
        }

        .home-service-page .section-sub-title .head-title {
          padding-bottom: 24px !important;
          margin-bottom: 22px !important;
        }

        .home-wrap p {
          margin-bottom: 0.7rem !important;
        }

        .home-wrap h2,
        .home-wrap h3,
        .home-wrap h4 {
          margin-top: 12px !important;
          margin-bottom: 10px !important;
        }

        /* ---------- HERO (never hidden by default) ---------- */
        .home-hero {
          position: relative;
          border-radius: 22px;
          overflow: hidden;
          border: 1px solid rgba(255,255,255,0.14);
          background: rgba(255,255,255,0.06);
          box-shadow: 0 28px 95px rgba(0,0,0,0.40);
          margin: 0 auto 14px;

          /* ✅ visible always */
          opacity: 1;
          transform: none;
          filter: none;
        }

        .home-hero video {
          width: 100%;
          height: auto;
          object-fit: contain;
          display: block;
          border: 0;
          transform: none;
          filter: saturate(1.04) contrast(1.02) brightness(0.86);
          transition: transform 1600ms cubic-bezier(.2,.9,.2,1), filter 1400ms ease;
        }

        .home-hero img,
        .home-hero iframe {
          width: 100%;
          height: 460px;
          object-fit: cover;
          display: block;
          border: 0;
          transform: scale(1.05);
          filter: saturate(1.04) contrast(1.02) brightness(0.86);
          transition: transform 1600ms cubic-bezier(.2,.9,.2,1), filter 1400ms ease;
        }

        .home-wrap.is-active .home-hero video {
          transform: none;
          filter: saturate(1.12) contrast(1.05) brightness(1);
        }

        .home-wrap.is-active .home-hero img,
        .home-wrap.is-active .home-hero iframe {
          transform: scale(1);
          filter: saturate(1.12) contrast(1.05) brightness(1);
        }

        .home-heroOverlay {
          position: absolute;
          inset: 0;
          background:
            radial-gradient(circle at 22% 10%, rgba(255,255,255,0.18), rgba(0,0,0,0.78)),
            linear-gradient(180deg, rgba(0,0,0,0.18), rgba(0,0,0,0.82));
          pointer-events: none;
        }

        /* Sheen sweep */
        .home-sheen {
          position: absolute;
          top: -30%;
          left: -65%;
          width: 65%;
          height: 180%;
          background: linear-gradient(
            90deg,
            rgba(255,255,255,0) 0%,
            rgba(255,255,255,0.20) 45%,
            rgba(255,255,255,0) 100%
          );
          transform: skewX(-18deg);
          opacity: 0;
          pointer-events: none;
        }
        .home-wrap.is-active .home-sheen {
          opacity: 1;
          animation: homeSheen 1200ms ease forwards;
        }
        @keyframes homeSheen {
          0% { transform: translateX(0) skewX(-18deg); opacity: 0; }
          18% { opacity: 1; }
          100% { transform: translateX(260%) skewX(-18deg); opacity: 0; }
        }

        /* Floating badge */
        .home-heroBadge {
          position: absolute;
          left: 16px;
          bottom: 16px;
          z-index: 2;
          padding: 10px 14px;
          border-radius: 999px;
          background: rgba(0,0,0,0.40);
          border: 1px solid rgba(255,255,255,0.18);
          backdrop-filter: blur(10px);
          font-size: 12px;
          letter-spacing: 0.02em;
          opacity: 0;
          transform: translateY(14px);
          transition: opacity 650ms ease 350ms, transform 650ms ease 350ms;
        }
        .home-wrap.is-active .home-heroBadge {
          opacity: 1;
          transform: translateY(0);
        }

        /* ---------- TEXT (visible always) ---------- */
        .home-text {
          opacity: 1;
          transform: none;
          filter: none;
          transition: opacity 700ms ease, transform 800ms cubic-bezier(.2,.9,.2,1), filter 800ms ease;
        }
        .home-wrap.is-active .home-text {
          transform: translateY(0);
        }

        /* ---------- CTA GRID ---------- */
        .home-ctaGrid {
          margin-top: 12px;
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 10px;
        }

        .home-ctaBtn {
          position: relative;
          border: 0;
          border-radius: 16px;
          padding: 14px 14px;
          text-align: left;
          cursor: pointer;
          overflow: hidden;
          color: #fff;
          background: rgba(255,255,255,0.10);
          border: 1px solid rgba(255,255,255,0.16);
          box-shadow: 0 18px 45px rgba(0,0,0,0.24);
          transform: translateY(0);
          transition: transform 220ms ease, box-shadow 220ms ease, filter 220ms ease;
        }
        .home-ctaBtn:hover {
          transform: translateY(-3px);
          box-shadow: 0 28px 85px rgba(0,0,0,0.34);
          filter: saturate(1.08);
        }

        .home-ctaBtn .txt {
          position: relative;
          z-index: 2;
          font-weight: 650;
          letter-spacing: 0.01em;
          line-height: 1.25;
          display: inline-flex;
          align-items: center;
          gap: 10px;
        }
        .home-ctaBtn .txt::after {
          content: "➜";
          font-size: 18px;
          transform: translateX(0);
          transition: transform 220ms ease;
          opacity: 0.9;
        }
        .home-ctaBtn:hover .txt::after { transform: translateX(6px); }

        .home-ctaBtn::before {
          content: "";
          position: absolute;
          inset: 0;
          opacity: 0;
          background: linear-gradient(135deg, rgba(59,130,246,0.92), rgba(147,51,234,0.80));
          transition: opacity 220ms ease;
        }
        .home-ctaBtn:hover::before { opacity: 1; }

        .home-ctaBtn::after {
          content: "";
          position: absolute;
          top: -40%;
          left: -60%;
          width: 60%;
          height: 180%;
          background: linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.30) 45%, rgba(255,255,255,0) 100%);
          transform: skewX(-18deg);
          opacity: 0;
          pointer-events: none;
        }
        .home-ctaBtn:hover::after {
          opacity: 1;
          animation: homeCtaSheen 900ms ease forwards;
        }
        @keyframes homeCtaSheen {
          0% { transform: translateX(0) skewX(-18deg); opacity: 0; }
          18% { opacity: 1; }
          100% { transform: translateX(260%) skewX(-18deg); opacity: 0; }
        }

        .home-ctaBtn.is-primary {
          background: linear-gradient(135deg, rgba(34,197,94,0.92), rgba(16,185,129,0.80));
          border-color: rgba(255,255,255,0.18);
          animation: donatePulse 3.2s ease-in-out infinite;
        }
        @keyframes donatePulse {
          0%, 100% { box-shadow: 0 18px 45px rgba(0,0,0,0.24); }
          50% { box-shadow: 0 26px 75px rgba(16,185,129,0.25); }
        }

        /* ---------- PURPOSE CARD ---------- */
        .home-purpose {
          margin-top: 16px;
          border-radius: 18px;
          overflow: hidden;
          border: 1px solid rgba(255,255,255,0.12);
          background: rgba(255,255,255,0.06);
          box-shadow: 0 22px 70px rgba(0,0,0,0.34);
        }
        .home-purposeImg {
          position: relative;
          height: 210px;
          overflow: hidden;
        }
        .home-purposeImg img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transform: scale(1.08);
          transition: transform 1200ms cubic-bezier(.2,.9,.2,1);
          filter: saturate(1.04) contrast(1.02);
        }
        .home-wrap.is-active .home-purposeImg img { transform: scale(1); }
        .home-purposeImg::after {
          content: "";
          position: absolute;
          inset: 0;
          background: linear-gradient(180deg, rgba(0,0,0,0.08), rgba(0,0,0,0.72));
        }
        .home-purposeBody { padding: 16px; text-align: left; }

        /* ---------- AT A GLANCE TILES (visible always) ---------- */
        .home-tiles {
          margin-top: 14px;
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 10px;
        }
        .home-tile {
          position: relative;
          border-radius: 18px;
          overflow: hidden;
          height: 170px;
          border: 1px solid rgba(255,255,255,0.12);
          background: rgba(255,255,255,0.06);
          box-shadow: 0 18px 55px rgba(0,0,0,0.28);

          opacity: 1;
          transform: none;
          filter: none;

          transition: box-shadow 240ms ease;
        }
        .home-tile img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transform: scale(1.06);
          transition: transform 650ms cubic-bezier(.2,.9,.2,1), filter 650ms ease;
        }
        .home-tile::after {
          content: "";
          position: absolute;
          inset: 0;
          background: linear-gradient(180deg, rgba(0,0,0,0.05), rgba(0,0,0,0.70));
          opacity: 0.85;
          transition: opacity 260ms ease;
        }
        .home-tile:hover { box-shadow: 0 28px 85px rgba(0,0,0,0.38); }
        .home-tile:hover img { transform: scale(1.14); filter: saturate(1.12) contrast(1.05); }
        .home-tile:hover::after { opacity: 0.70; }

        .home-tileTag {
          position: absolute;
          left: 12px;
          bottom: 12px;
          z-index: 2;
          padding: 8px 12px;
          border-radius: 999px;
          background: rgba(255,255,255,0.12);
          border: 1px solid rgba(255,255,255,0.16);
          backdrop-filter: blur(10px);
          font-size: 12px;
          letter-spacing: 0.02em;
        }

        /* ---------- DONATION BAR ---------- */
        .home-donateBar {
          margin-top: 16px;
          border-radius: 18px;
          border: 1px solid rgba(255,255,255,0.14);
          background: linear-gradient(135deg, rgba(11,40,73,0.92), rgba(59,130,246,0.35));
          padding: 16px;
          display: flex;
          gap: 14px;
          align-items: center;
          justify-content: space-between;
          box-shadow: 0 22px 80px rgba(0,0,0,0.35);
          text-align: left;
        }
        .home-donateBtn {
          border: 0;
          border-radius: 14px;
          padding: 12px 16px;
          background: #fff;
          color: #0b2849;
          font-weight: 800;
          cursor: pointer;
          box-shadow: 0 18px 45px rgba(0,0,0,0.24);
          transition: transform 220ms ease, box-shadow 220ms ease, filter 220ms ease;
          white-space: nowrap;
        }
        .home-donateBtn:hover {
          transform: translateY(-3px);
          box-shadow: 0 28px 85px rgba(0,0,0,0.34);
          filter: saturate(1.06);
        }

        @media (max-width: 768px) {
          .home-service-page .block.color-scheme-4 {
            padding-top: 34px !important;
            padding-bottom: 22px !important;
          }
          .home-service-page .section-sub-title .head-title {
            padding-bottom: 14px !important;
            margin-bottom: 16px !important;
          }
          .home-wrap p {
            margin-bottom: 0.55rem !important;
          }
          .home-wrap h2,
          .home-wrap h3,
          .home-wrap h4 {
            margin-top: 9px !important;
            margin-bottom: 7px !important;
          }
          .home-wrap .head-title {
            font-size: 24px !important;
            line-height: 1.1 !important;
          }
          .home-wrap h2 {
            font-size: 16px !important;
            line-height: 1.2 !important;
          }
          .home-wrap h3 {
            font-size: 18px !important;
            line-height: 1.2 !important;
          }
          .home-wrap h4 {
            font-size: 14px !important;
            line-height: 1.25 !important;
          }
          .home-wrap p,
          .home-wrap li,
          .home-wrap .home-ctaBtn,
          .home-wrap .home-donateBar {
            font-size: 13px !important;
            line-height: 1.4 !important;
          }
          .home-wrap .home-ctaBtn .txt {
            font-size: 13px !important;
            line-height: 1.25 !important;
          }
          .home-hero video { height: auto; }
          .home-hero img, .home-hero iframe { height: 240px; }
          .home-ctaGrid { grid-template-columns: 1fr; }
          .home-purpose { margin-top: 12px; }
          .home-purposeImg { height: 180px; }
          .home-tiles { grid-template-columns: 1fr; }
          .home-tile { height: 145px; }
          .home-donateBar { flex-direction: column; align-items: stretch; }
          .home-donateBtn { width: 100%; }
        }

        @media (prefers-reduced-motion: reduce) {
          .home-hero video,
          .home-hero img,
          .home-hero iframe,
          .home-sheen,
          .home-ctaBtn,
          .home-ctaBtn::after,
          .home-purposeImg img,
          .home-tile img,
          .home-donateBtn {
            transition: none !important;
            animation: none !important;
            transform: none !important;
            filter: none !important;
          }
        }
      `}</style>

      <section className="home-service-page">
        <div className="block color-scheme-4">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-md-10 col-lg-9">
                <div className="section-sub-title center">
                  <article
                    ref={ref}
                    className={`section-title-body white home-wrap ${active ? "is-active" : ""}`}
                  >
                    {/* Title */}
                    <div className="home-text">
                      <h1 className="head-title">
                        <span>
                          Festival of Globe — Celebrating Culture, Leadership,
                          Community &amp; Civic Empowerment
                        </span>
                      </h1>
                    </div>

                    {/* HERO SECTION */}
                    <div className="home-hero">
                      {HOME_ASSETS.hero.useYoutube ? (
                        <iframe
                          src={HOME_ASSETS.hero.youtube}
                          title="Festival of Globe Home Montage"
                          loading="lazy"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                        />
                      ) : (
                        <video
                          ref={heroVideoRef}
                          src={HOME_ASSETS.hero.videoMp4}
                          muted
                          loop
                          playsInline
                          preload="none"
                          poster={HOME_ASSETS.hero.image}
                        />
                      )}
                      <div className="home-heroOverlay" />
                      <div className="home-sheen" />
                      <div className="home-heroBadge">
                        Culture • Community • Civic Empowerment
                      </div>
                    </div>

                    <div className="home-text">
                      <h2 className="white" style={{ marginTop: 8 }}>
                        Celebrate Your Life with the Hindu Way of Living
                      </h2>

                      <p className="white" style={{ textAlign: "left" }}>
                        Festival of Globe (FOG) unites culture, community, civic
                        participation and leadership development into one
                        national platform dedicated to uplifting Hindu Americans
                        and pro-Hindu allies across the United States. Through
                        festivals, education, youth programs, and leadership
                        training, we strengthen identity, amplify our collective
                        voice, and empower every generation to lead with
                        confidence.
                      </p>

                      <p className="white" style={{ textAlign: "left" }}>
                        <strong>Sub-headline</strong>
                        <br />
                        A unified digital home bringing together:
                        <br />
                        <strong>
                          Culture • Community • Civic Engagement • Education •
                          Leadership • Advocacy
                        </strong>
                      </p>

                      <h3 className="white" style={{ textAlign: "left" }}>
                        Key Experience Buttons
                      </h3>

                      <div className="home-ctaGrid">
                        {quickLinks.map((b, idx) => (
                          <button
                            key={idx}
                            type="button"
                            className={`home-ctaBtn ${b.primary ? "is-primary" : ""}`}
                            onClick={b.onClick}
                          >
                            <span className="txt">{b.label}</span>
                          </button>
                        ))}
                      </div>

                      {/* OUR PURPOSE */}
                      <div className="home-purpose">
                        <div className="home-purposeImg">
                          <img
                            src={HOME_ASSETS.purpose.image}
                            alt="Our Purpose"
                            loading="lazy"
                          />
                        </div>
                        <div className="home-purposeBody">
                          <h3 className="white" style={{ marginTop: 8 }}>
                            OUR PURPOSE
                          </h3>
                          <p className="white" style={{ marginBottom: 0 }}>
                            Festival of Globe and Americans4Hindus (A4H) have
                            come together to create a unified national ecosystem
                            that celebrates vibrant Hindu culture while
                            mobilizing communities toward civic responsibility
                            and political empowerment. This platform bridges
                            cultural expression, youth mentorship, policy
                            education, health &amp; wellness programs, and
                            grassroots civic participation.
                          </p>
                        </div>
                      </div>

                      {/* WHAT WE DO AT A GLANCE */}
                      <div style={{ marginTop: 18 }}>
                        <h3 className="white" style={{ textAlign: "left" }}>
                          WHAT WE DO AT A GLANCE
                        </h3>

                        <div className="home-tiles">
                          <div className="home-tile">
                            <img
                              src={HOME_ASSETS.atAGlance.culture}
                              alt="Celebrate Culture"
                              loading="lazy"
                            />
                            <div className="home-tileTag">Celebrate Culture</div>
                          </div>

                          <div className="home-tile">
                            <img
                              src={HOME_ASSETS.atAGlance.leaders}
                              alt="Empower Leaders"
                              loading="lazy"
                            />
                            <div className="home-tileTag">Empower Leaders</div>
                          </div>

                          <div className="home-tile">
                            <img
                              src={HOME_ASSETS.atAGlance.civic}
                              alt="Strengthen Civic Engagement"
                              loading="lazy"
                            />
                            <div className="home-tileTag">
                              Strengthen Civic Engagement
                            </div>
                          </div>

                          <div className="home-tile">
                            <img
                              src={HOME_ASSETS.atAGlance.alliances}
                              alt="Build Community Alliances"
                              loading="lazy"
                            />
                            <div className="home-tileTag">
                              Build Community Alliances
                            </div>
                          </div>
                        </div>

                        <div style={{ marginTop: 14, textAlign: "left" }}>
                          <h4 className="white">Celebrate Culture</h4>
                          <p className="white">
                            Through spectacular festivals, parades, competitions,
                            and community gatherings, FOG brings families and
                            communities together. The annual India Day Parade,
                            cultural showcases, classical and Bollywood
                            competitions, FOG Idol, traditional dances, film
                            awards, and Grand Marshal appearances make FOG one
                            of the biggest Indian diaspora celebrations in North
                            America.
                          </p>

                          <h4 className="white">Empower Leaders</h4>
                          <p className="white">
                            Our training programs prepare youth, women,
                            professionals, and community members to become
                            confident leaders in civic, public policy, and
                            political spaces.
                          </p>

                          <h4 className="white">Strengthen Civic Engagement</h4>
                          <p className="white">
                            Through voter education, policy briefings, training
                            academies, and mobilization drives, we ensure every
                            Hindu American can participate confidently in the
                            U.S. democratic process.
                          </p>

                          <h4 className="white">Build Community Alliances</h4>
                          <p className="white">
                            We foster local and national partnerships across
                            religious, cultural, professional, and policy
                            spheres — including Hindu-Jewish, Hindu-Christian,
                            Hindu-Latino, and interfaith networks.
                          </p>
                        </div>
                      </div>

                      {/* HOME PAGE VIDEO */}
                      <div style={{ marginTop: 18, textAlign: "left" }}>
                        <h3 className="white">HOME PAGE VIDEO</h3>
                        <p className="white" style={{ marginBottom: 0 }}>
                          A dynamic montage of Festival of Globe parades,
                          dignitary appearances, Grand Marshals, dance and music
                          competitions, Rajneeti Academy classrooms, youth
                          leadership sessions, voter outreach drives, health
                          fairs, and community celebrations.
                        </p>
                      </div>

                      {/* DONATION ONE-LINER */}
                      <div className="home-donateBar">
                        <div className="white" style={{ fontWeight: 650 }}>
                          Support culture, leadership, and civic empowerment —
                          your contribution builds the future of our community.
                        </div>
                        <button
                          type="button"
                          className="home-donateBtn"
                          onClick={handleDonate}
                        >
                          Donate Now
                        </button>
                      </div>
                    </div>
                  </article>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default HomePage;
